import React, { useState } from 'react';

import { Box, Flex, Modal, ModalBody, ModalContent, ModalOverlay, Tooltip, useDisclosure } from '@chakra-ui/react';
import { Button, Menu, MenuItem } from '@material-ui/core';
import useForceUpdate from 'components/hooks/useForceUpdate';
import { useToast } from 'components/hooks/useToast';
import { addHours, isPast } from 'date-fns';
import styled from 'styled-components';

import { CurvedBackArrow, Ellipsis, Notifications } from 'assets/assetsV1/iconComponents';

import { localStorageKeys } from 'constants/localStorageKeys';
import { sendClickedOnWithdraw } from 'constants/mixpanelHelpers/reviewsEvents';

import { reviewNotify } from 'services/analytics/review';
import { withdrawNomination } from 'services/performanceReview';

import { Button as ButtonComp } from 'uiToolkitV1/Button';
import { TextBodyMedium, TextHeader2 } from 'uiToolkitV1/Typography';

const StyledMenuItem = styled(MenuItem)`
  padding: 8px 16px 8px 6px;
  border-radius: 4px;
  font-family: inherit;
  &:hover {
    color: #104f95;
    background-color: #e6f1fd;
    svg {
      color: #104f95;
    }
  }
`;

const StyledMenu = styled(Menu)`
  ul {
    padding: 8px;
    margin-top: 0;
  }
`;

export const WithdrawConfirmation = ({ displayName, onCancel, onAccept }) => (
  <Box p={3}>
    <Box p={3} mb={6}>
      <TextHeader2 bold>Withdraw Nomination?</TextHeader2>

      <TextBodyMedium>Do you want to withdraw peer nomination for {displayName}?</TextBodyMedium>
    </Box>

    <Flex justifyContent="flex-end" alignItems="center" p={3}>
      <ButtonComp variant="outlined" onClick={onCancel} mr={4} w="110px">
        Cancel
      </ButtonComp>
      <ButtonComp onClick={onAccept} w="110px">
        Withdraw
      </ButtonComp>
    </Flex>
  </Box>
);

export const ReviewsPendingOnOthersMenu = ({ data, removeFromReviewsList }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [, forceUpdate] = useForceUpdate();
  const { review, employee, allowReviewFormWithdraw } = Object.values(data)[0]?.reviewItem ?? {};

  const handleClick = (event) => {
    if (anchorEl !== event.currentTarget) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleWithdraw = async () => {
    await withdrawNomination({ reviewId: review.feedbackForm.id, providerEmpId: employee.id });
    removeFromReviewsList(review.feedbackForm.id, employee.id);
    onClose();
  };

  const handleNotify = async () => {
    try {
      const response = await reviewNotify({
        feedbackFormId: review.feedbackForm.id,
        receiverEmployeeId: review.receiverEmployeeId,
        providerEmployeeId: review.providerEmployeeId,
        providerType: review.providerType,
      });
      localStorage.setItem(
        `${localStorageKeys.lastNotifiedReviewTime}-${review.feedbackForm.id}-${review.providerEmployeeId}`,
        addHours(new Date(), 24)
      );
      toast({
        title: response.message,
        status: 'success',
      });
      forceUpdate();
    } catch (error) {
      console.log(error);
    }
  };

  const isNotifyDisable = isPast(
    new Date(
      localStorage.getItem(
        `${localStorageKeys.lastNotifiedReviewTime}-${review.feedbackForm.id}-${review.providerEmployeeId}`
      )
    )
  );

  return (
    <Box>
      <Button
        aria-controls="simple-menu"
        aria-haspopup="true"
        style={{ backgroundColor: anchorEl ? '#e6f1fd' : null, minWidth: 'unset' }}
        onClick={handleClick}
      >
        <Ellipsis boxSize={7} />
      </Button>
      <StyledMenu
        id="simple-menu"
        anchorEl={anchorEl}
        keepMounted
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={open}
        onClose={handleClose}
      >
        <Tooltip
          label="You have notified, Please wait 24 hours before you notify again "
          placement="top-end"
          shouldWrapChildren
          isDisabled={isNotifyDisable}
        >
          <StyledMenuItem onClick={handleNotify} disabled={!isNotifyDisable}>
            <Flex alignItems="center">
              <Notifications boxSize="16px" mr={1} color={isNotifyDisable && 'button.blue.focus'} />
              <TextBodyMedium _hover={{ color: isNotifyDisable && 'button.blue.focus' }}>
                {!isNotifyDisable ? 'Notified' : 'Notify'}
              </TextBodyMedium>
            </Flex>
          </StyledMenuItem>
        </Tooltip>
        {allowReviewFormWithdraw && (
          <Flex className="action" justify="flex-end" w="100%">
            <StyledMenuItem
              onClick={() => {
                sendClickedOnWithdraw({
                  eventProperties: {
                    reviewCycleId: review.feedbackForm.id,
                    empId: review.receiverEmployeeId,
                    source: 'MyReviewsPendingOnOthers',
                  },
                });
                onOpen();
              }}
            >
              <CurvedBackArrow boxSize="16px" mr={1} />
              <TextBodyMedium _hover={{ color: 'button.blue.focus' }}>Withdraw</TextBodyMedium>
            </StyledMenuItem>
            <Modal isOpen={isOpen} onClose={onClose}>
              <ModalOverlay />
              <ModalContent minW="550px" mt="12%">
                <ModalBody p={0}>
                  <WithdrawConfirmation
                    displayName={employee.displayName}
                    onCancel={onClose}
                    onAccept={handleWithdraw}
                  />
                </ModalBody>
              </ModalContent>
            </Modal>
          </Flex>
        )}
      </StyledMenu>
    </Box>
  );
};
